'use client';
import type { ReactElement } from 'react';
import { FlowchartIcon } from './flowchart-constants';
/**
 * Real SVG shape icons (issue #4) — one clean line-art glyph per
 * {@link FlowchartIcon}, drawn with `currentColor` so the host can tint it with
 * the node's accent. These replace the old unicode text glyphs and are rendered
 * centered *inside* the shape body (the node stacks the icon above the label),
 * so the decision "?" sits inset within the diamond rather than in a corner.
 */
const ICON_VIEWBOX = '0 0 24 24';
const STROKE_WIDTH = 1.8;
function QuestionGlyph(): ReactElement {
    // A question mark sized to sit inset/centered within the diamond.
    return (<>
      <path d="M9 9a3 3 0 1 1 4.2 2.75c-0.9 0.4 -1.2 1 -1.2 2" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="17.5" r="1.1" fill="currentColor" stroke="none"/>
    </>);
}
function DatabaseGlyph(): ReactElement {
    return (<>
      <ellipse cx={12} cy={6.5} rx={6.5} ry={2.6} fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH}/>
      <path d="M5.5 6.5v11c0 1.44 2.91 2.6 6.5 2.6s6.5 -1.16 6.5 -2.6v-11" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round"/>
      <path d="M5.5 12c0 1.44 2.91 2.6 6.5 2.6s6.5 -1.16 6.5 -2.6" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round"/>
    </>);
}
function DocumentGlyph(): ReactElement {
    return (<>
      <path d="M7 3.5h7l3.5 3.5v11.5a1.5 1.5 0 0 1 -1.5 1.5H7a1.5 1.5 0 0 1 -1.5 -1.5V5a1.5 1.5 0 0 1 1.5 -1.5Z" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinejoin="round"/>
      <path d="M14 3.5V7h3.5" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinejoin="round"/>
      <path d="M9 12h6M9 15.5h6" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round"/>
    </>);
}
function IoGlyph(): ReactElement {
    // Opposed I/O arrows: input down, output up.
    return (<>
      <path d="M8 4v9m0 0 -2.5 -2.5M8 13l2.5 -2.5" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 20v-9m0 0 -2.5 2.5M16 11l2.5 2.5" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round" strokeLinejoin="round"/>
    </>);
}
function GearGlyph(): ReactElement {
    return (<>
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH}/>
      <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.55 1.55M16.45 16.45 18 18M18 6l-1.55 1.55M7.55 16.45 6 18" fill="none" stroke="currentColor" strokeWidth={STROKE_WIDTH} strokeLinecap="round"/>
    </>);
}
const GLYPH_BY_ICON: Record<FlowchartIcon, () => ReactElement> = {
    [FlowchartIcon.Question]: QuestionGlyph,
    [FlowchartIcon.Database]: DatabaseGlyph,
    [FlowchartIcon.Document]: DocumentGlyph,
    [FlowchartIcon.IO]: IoGlyph,
    [FlowchartIcon.Gear]: GearGlyph,
};
/** Props for {@link FlowchartShapeIcon}. */
export interface FlowchartShapeIconProps {
    icon: FlowchartIcon;
    /** Accent color for the glyph stroke/fill. */
    color: string;
    /** Rendered glyph size in px (square). */
    size: number;
}
/** Renders the real SVG glyph for a flowchart shape icon, tinted by `color`. */
export function FlowchartShapeIcon({ icon, color, size }: FlowchartShapeIconProps): ReactElement {
    const Glyph = GLYPH_BY_ICON[icon];
    return (
      <svg width={size} height={size} viewBox={ICON_VIEWBOX} style={{ display: 'block', color, flexShrink: 0, pointerEvents: 'none' }} aria-hidden>
        <Glyph />
      </svg>
    );
}
